import { observer } from 'mobx-react-lite';
import { Loader2, RotateCcw, ArrowUp } from 'lucide-react';
import { backupStore } from '../../../stores/backup.store';
import { cn } from '@/utils/cn';

const BACKUP_STEP: Record<string, string> = {
  zipping: 'Backup wird erstellt …',
  uploading: 'Wird in die Cloud hochgeladen …',
  complete: 'Backup fertig',
  error: 'Backup fehlgeschlagen',
};
const RESTORE_STEP: Record<string, string> = {
  downloading: 'Backup wird aus der Cloud geladen …',
  stopping: 'Server wird gestoppt …',
  extracting: 'Dateien werden wiederhergestellt …',
  finalizing: 'Wird abgeschlossen …',
  complete: 'Wiederherstellung fertig',
  error: 'Wiederherstellung fehlgeschlagen',
};

const Bar = ({ value, tone }: { value: number; tone: 'ember' | 'warn' }) => (
  <div className="h-2 rounded-full bg-white/[0.06] overflow-hidden">
    <div
      className={cn('h-full rounded-full transition-all duration-500', tone === 'ember' ? 'bg-ember' : 'bg-warn')}
      style={{ width: `${Math.max(4, Math.min(100, value))}%` }}
    />
  </div>
);

/** Laufendes Backup bzw. laufende Wiederherstellung dieses Servers – mit Abbrechen. */
export const BackupProgressCard = observer(({ serverId }: { serverId: string }) => {
  const backupRunning = backupStore.backupStatus !== 'idle' && backupStore.backupServerId === serverId;
  const restoreRunning = backupStore.restoreStatus !== 'idle' && backupStore.restoreServerId === serverId;
  if (!backupRunning && !restoreRunning) return null;

  if (restoreRunning) {
    return (
      <div className="rounded-2xl border border-warn/30 bg-warn/[0.06] p-5 space-y-3">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5 text-sm font-medium text-warn">
            <RotateCcw size={16} className="animate-spin" />
            {RESTORE_STEP[backupStore.restoreStatus] || 'Wiederherstellung läuft …'}
          </div>
          <span className="font-mono text-sm text-warn">{Math.round(backupStore.restoreProgress)}%</span>
        </div>
        <Bar value={backupStore.restoreProgress} tone="warn" />
        {backupStore.restoreMessage && <p className="text-xs text-fg-muted">{backupStore.restoreMessage}</p>}
      </div>
    );
  }

  const uploading = backupStore.backupStatus === 'uploading';
  const eta = backupStore.systemUploadSpeed > 0 && backupStore.backupTotalBytes
    ? Math.round((backupStore.backupTotalBytes - backupStore.backupUploadedBytes) / backupStore.systemUploadSpeed)
    : 0;

  return (
    <div className="rounded-2xl border border-ember/30 bg-ember/[0.05] p-5 space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2.5 text-sm font-medium text-fg">
          <Loader2 size={16} className="animate-spin text-ember" />
          {BACKUP_STEP[backupStore.backupStatus] || 'Backup läuft …'}
          {backupStore.backupTotalBytes > 0 && (
            <span className="font-mono text-xs text-fg-muted">
              {(backupStore.backupUploadedBytes / 1024 / 1024).toFixed(0)} / {(backupStore.backupTotalBytes / 1024 / 1024).toFixed(0)} MB
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 text-xs">
          {uploading && (
            <>
              <span className="inline-flex items-center gap-1 text-online"><ArrowUp size={12} /> {backupStore.totalSpeed} MB/s</span>
              {eta > 0 && <span className="text-fg-muted font-mono">noch {backupStore.formatEta(eta)}</span>}
            </>
          )}
          <span className="font-mono text-ember">{Math.round(backupStore.backupProgress)}%</span>
          <button onClick={() => backupStore.handleCancelBackup()} className="text-fg-muted hover:text-red-400 transition-colors">
            Abbrechen
          </button>
        </div>
      </div>
      <Bar value={backupStore.backupProgress} tone="ember" />
      <div className="text-[11px] text-fg-muted">
        Hängt das Backup?{' '}
        <button onClick={() => backupStore.forceCancelBackup(serverId)} className="text-red-400 hover:underline">
          Zurücksetzen
        </button>
      </div>
    </div>
  );
});
